import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  ReactNode,
} from "react";
import { useAppSelector } from "@/store/hooks";
import { SOCKET_BASE_URL } from "@/constants/api/config";

type WebSocketStatus = "connecting" | "connected" | "disconnected" | "error";

interface WebSocketContextType {
  status: Record<string, WebSocketStatus>;
  connect: (path: string, callback: (data: any) => void) => () => void;
  disconnect: (path: string) => void;
}

const WebSocketContext = createContext<WebSocketContextType | null>(null);

const MAX_RECONNECT_ATTEMPTS = 5;
const MAX_RECONNECT_DELAY = 30000;
const MAX_IMMEDIATE_FAILURES = 3;

const INVALID_PATH_MARKER = "INVALID_PATH_PREVENT_CONNECTION";

export const WebSocketProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const token = useAppSelector((state) => state.auth.token);

  const sockets = useRef<Record<string, WebSocket>>({});
  const callbacks = useRef<Record<string, (data: any) => void>>({});
  const reconnectAttempts = useRef<Record<string, number>>({});
  const reconnectTimeouts = useRef<Record<string, number>>({});
  const immediateFailures = useRef<Record<string, number>>({});
  const connecting = useRef<Set<string>>(new Set());

  const [status, setStatus] = useState<Record<string, WebSocketStatus>>({});

  const normalizePath = useCallback((path: string): string | null => {
    if (!path || typeof path !== "string" || path.trim() === "") return null;
    if (path === INVALID_PATH_MARKER || path.includes(INVALID_PATH_MARKER))
      return null;

    const normalized = path.startsWith("/") ? path.slice(1) : path;
    if (!normalized || normalized.trim() === "") return null;

    return normalized;
  }, []);

  // ✅ IMPORTANT FIX:
  // Ensure the endpoint format matches backend routing:
  // "workqueue/?limit=10&page=1" instead of "workqueue?limit=10&page=1"
  const ensureTrailingSlashBeforeQuery = useCallback((p: string) => {
    const qIndex = p.indexOf("?");
    if (qIndex === -1) {
      // no query string
      return p.endsWith("/") ? p : `${p}/`;
    }
    const pathPart = p.slice(0, qIndex);
    const queryPart = p.slice(qIndex + 1);
    const fixedPath = pathPart.endsWith("/") ? pathPart : `${pathPart}/`;
    return `${fixedPath}?${queryPart}`;
  }, []);

  const clearReconnectTimeout = useCallback((path: string) => {
    const t = reconnectTimeouts.current[path];
    if (t) {
      window.clearTimeout(t);
      delete reconnectTimeouts.current[path];
    }
  }, []);

  const buildUrl = useCallback(
    (normalizedPath: string) => {
      const base = SOCKET_BASE_URL
        ? SOCKET_BASE_URL.endsWith("/")
          ? SOCKET_BASE_URL
          : `${SOCKET_BASE_URL}/`
        : "";

      const separator = normalizedPath.includes("?") ? "&" : "?";
      return `${base}${normalizedPath}${separator}Token=${encodeURIComponent(
        token as string,
      )}`;
    },
    [token],
  );

  const closeSocketInternal = useCallback(
    (path: string, code = 1000, reason = "Client closed") => {
      clearReconnectTimeout(path);
      connecting.current.delete(path);

      const ws = sockets.current[path];
      if (ws) {
        try {
          delete sockets.current[path];
          if (
            ws.readyState === WebSocket.OPEN ||
            ws.readyState === WebSocket.CONNECTING
          ) {
            ws.close(code, reason);
          }
        } catch (err) {
          console.error(
            `[WebSocketContext] Error closing socket ${path}:`,
            err,
          );
        }
      }

      delete callbacks.current[path];
      reconnectAttempts.current[path] = 0;
      immediateFailures.current[path] = 0;

      setStatus((prev) => ({ ...prev, [path]: "disconnected" }));
    },
    [clearReconnectTimeout],
  );

  const connectRef = useRef<
    (path: string, callback: (data: any) => void) => () => void
  >(() => () => {});

  const connect = useCallback(
    (path: string, callback: (data: any) => void) => {
      const normalized0 = normalizePath(path);
      if (!normalized0) {
        console.warn("[WebSocketContext] connect blocked: invalid path", {
          path,
        });
        return () => {};
      }

      const normalizedPath = ensureTrailingSlashBeforeQuery(normalized0);

      if (!callback || typeof callback !== "function") {
        console.warn("[WebSocketContext] connect blocked: invalid callback", {
          path: normalizedPath,
        });
        return () => {};
      }

      if (!SOCKET_BASE_URL) {
        console.warn("[WebSocketContext] BLOCKED: SOCKET_BASE_URL missing");
        setStatus((prev) => ({ ...prev, [normalizedPath]: "error" }));
        return () => {};
      }

      if (!token || typeof token !== "string" || token.trim() === "") {
        console.warn("[WebSocketContext] BLOCKED: token missing/invalid");
        setStatus((prev) => ({ ...prev, [normalizedPath]: "error" }));
        return () => {};
      }

      callbacks.current[normalizedPath] = callback;

      const existing = sockets.current[normalizedPath];
      if (
        existing &&
        (existing.readyState === WebSocket.OPEN ||
          existing.readyState === WebSocket.CONNECTING)
      ) {
        return () =>
          closeSocketInternal(normalizedPath, 1000, "Client disconnected");
      }

      if (connecting.current.has(normalizedPath)) {
        return () =>
          closeSocketInternal(normalizedPath, 1000, "Client disconnected");
      }

      connecting.current.add(normalizedPath);
      clearReconnectTimeout(normalizedPath);

      setStatus((prev) => ({ ...prev, [normalizedPath]: "connecting" }));

      const url = buildUrl(normalizedPath);
      console.log(`[WebSocketContext] Connecting: ${normalizedPath} -> ${url}`);

      let ws: WebSocket;
      try {
        ws = new WebSocket(url);
      } catch (err) {
        console.error(
          `[WebSocketContext] Failed to create WebSocket for ${normalizedPath}:`,
          err,
        );
        connecting.current.delete(normalizedPath);
        setStatus((prev) => ({ ...prev, [normalizedPath]: "error" }));
        return () => {};
      }

      sockets.current[normalizedPath] = ws;

      ws.onopen = () => {
        connecting.current.delete(normalizedPath);
        reconnectAttempts.current[normalizedPath] = 0;
        immediateFailures.current[normalizedPath] = 0;
        setStatus((prev) => ({ ...prev, [normalizedPath]: "connected" }));
        console.log(`[WebSocketContext] Connected: ${normalizedPath}`);
      };

      ws.onmessage = (event) => {
        // Robust logging for debugging (DEV mode)
        const isDev = process.env.NODE_ENV === "development";
        if (isDev) {
          console.log("[WS RAW]", normalizedPath, event.data);
        }

        let parsed: any;
        try {
          parsed = JSON.parse(event.data);
        } catch (err) {
          console.error(
            `[WebSocketContext] JSON parse error on ${normalizedPath}:`,
            err,
          );
          return;
        }

        if (isDev) {
          console.log("[WS PARSED]", normalizedPath, parsed);
        }

        const cb = callbacks.current[normalizedPath];
        if (!cb) return;

        try {
          cb(parsed);
        } catch (err) {
          console.error(
            `[WebSocketContext] Callback error on ${normalizedPath}:`,
            err,
          );
        }
      };

      ws.onerror = (error) => {
        console.error(`[WebSocketContext] Error on ${normalizedPath}:`, error);
        console.error(
          `[WebSocketContext] Error details - URL: ${url}, readyState: ${ws.readyState}`,
        );
        setStatus((prev) => ({ ...prev, [normalizedPath]: "error" }));
        connecting.current.delete(normalizedPath);

        if (ws.readyState === WebSocket.CLOSED) {
          const failures = (immediateFailures.current[normalizedPath] || 0) + 1;
          immediateFailures.current[normalizedPath] = failures;

          console.warn(
            `[WebSocketContext] Immediate failure for ${normalizedPath}. Consecutive: ${failures}/${MAX_IMMEDIATE_FAILURES}`,
          );

          if (failures >= MAX_IMMEDIATE_FAILURES) {
            console.error(
              `[WebSocketContext] Stopping reconnection for ${normalizedPath} - too many immediate failures. Server may be rejecting.`,
            );
            delete callbacks.current[normalizedPath];
            clearReconnectTimeout(normalizedPath);
          }
        }
      };

      ws.onclose = (event) => {
        connecting.current.delete(normalizedPath);

        console.log(
          `[WebSocketContext] Closed: ${normalizedPath}`,
          event.code,
          event.reason,
        );

        if (sockets.current[normalizedPath] === ws) {
          delete sockets.current[normalizedPath];
        }

        setStatus((prev) => ({ ...prev, [normalizedPath]: "disconnected" }));

        const cb = callbacks.current[normalizedPath];

        if (
          event.code !== 1000 &&
          cb &&
          token &&
          typeof token === "string" &&
          token.trim() !== ""
        ) {
          const attempts = reconnectAttempts.current[normalizedPath] || 0;

          if (attempts < MAX_RECONNECT_ATTEMPTS) {
            const delay = Math.min(1000 * 2 ** attempts, MAX_RECONNECT_DELAY);
            reconnectAttempts.current[normalizedPath] = attempts + 1;

            console.log(
              `[WebSocketContext] Reconnecting ${normalizedPath} in ${delay}ms (attempt ${
                attempts + 1
              }/${MAX_RECONNECT_ATTEMPTS})`,
            );

            clearReconnectTimeout(normalizedPath);
            reconnectTimeouts.current[normalizedPath] = window.setTimeout(
              () => {
                const currentCb = callbacks.current[normalizedPath];
                if (
                  currentCb &&
                  token &&
                  typeof token === "string" &&
                  token.trim() !== ""
                ) {
                  connectRef.current(normalizedPath, currentCb);
                }
                delete reconnectTimeouts.current[normalizedPath];
              },
              delay,
            );
          } else {
            console.warn(
              `[WebSocketContext] Max reconnect attempts reached for ${normalizedPath}. Stopping.`,
            );
            setStatus((prev) => ({ ...prev, [normalizedPath]: "error" }));
            delete callbacks.current[normalizedPath];
            clearReconnectTimeout(normalizedPath);
          }
        } else {
          reconnectAttempts.current[normalizedPath] = 0;
          immediateFailures.current[normalizedPath] = 0;
          clearReconnectTimeout(normalizedPath);
        }
      };

      return () =>
        closeSocketInternal(normalizedPath, 1000, "Client disconnected");
    },
    [
      token,
      normalizePath,
      ensureTrailingSlashBeforeQuery,
      buildUrl,
      clearReconnectTimeout,
      closeSocketInternal,
    ],
  );

  useEffect(() => {
    connectRef.current = connect;
  }, [connect]);

  const disconnect = useCallback(
    (path: string) => {
      const normalized0 = normalizePath(path);
      if (!normalized0) return;
      const normalizedPath = ensureTrailingSlashBeforeQuery(normalized0);
      closeSocketInternal(normalizedPath, 1000, "Client disconnected");
    },
    [normalizePath, ensureTrailingSlashBeforeQuery, closeSocketInternal],
  );

  useEffect(() => {
    if (token && typeof token === "string" && token.trim() !== "") return;

    console.log("[WebSocketContext] Token cleared - closing all sockets");

    Object.keys(reconnectTimeouts.current).forEach((p) =>
      window.clearTimeout(reconnectTimeouts.current[p]),
    );
    reconnectTimeouts.current = {};

    Object.keys(sockets.current).forEach((p) => {
      try {
        sockets.current[p]?.close(1000, "Token cleared");
      } catch (err) {
        console.error(`[WebSocketContext] Error closing ${p}:`, err);
      }
    });

    sockets.current = {};
    callbacks.current = {};
    reconnectAttempts.current = {};
    immediateFailures.current = {};
    connecting.current.clear();
    void Promise.resolve().then(() => {
      setStatus({});
    });
  }, [token]);

  useEffect(() => {
    const connectingSet = connecting.current;
    return () => {
      Object.keys(reconnectTimeouts.current).forEach((p) =>
        window.clearTimeout(reconnectTimeouts.current[p]),
      );

      Object.keys(sockets.current).forEach((p) => {
        try {
          sockets.current[p]?.close(1000, "Provider unmount");
        } catch (err) {
          console.error(`[WebSocketContext] Error closing ${p}:`, err);
        }
      });

      sockets.current = {};
      callbacks.current = {};
      reconnectAttempts.current = {};
      immediateFailures.current = {};
      reconnectTimeouts.current = {};
      connectingSet.clear();
    };
  }, []);

  return (
    <WebSocketContext.Provider value={{ status, connect, disconnect }}>
      {children}
    </WebSocketContext.Provider>
  );
};

export const useWebSocketContext = () => {
  const ctx = useContext(WebSocketContext);
  if (!ctx) {
    throw new Error(
      "useWebSocketContext must be used within a WebSocketProvider",
    );
  }
  return ctx;
};
